import { cn } from "@/lib/utils";
import {
  AlertCircle,
  CheckCircle,
  Loader2,
  MinusCircle,
  PlayCircle,
  XCircle,
} from "lucide-react";
import { useEffect } from "react";

import { ActionItemExecution } from "@cohesive/api-entities";
import {
  BaseEntityCard,
  BaseEntityCardActions,
  BaseEntityCardBody,
  BaseEntityCardContent,
  BaseEntityCardContextMenu,
  BaseEntityCardDescription,
  BaseEntityCardHeader,
  BaseEntityCardIcon,
  BaseEntityCardProps,
  BaseEntityCardTitle,
} from "../BaseCard";

export type ActionItemExecutionCardProps = ActionItemExecution &
  BaseEntityCardProps & {
    /** Whether to show shimmer effect on the subtitle */
    current_activity?: string;
    status?: ActionItemExecutionStatus;
    isLoading?: boolean;
  };

export type ActionItemExecutionStatus =
  | "started"
  | "running"
  | "needs_attention"
  | "completed"
  | "failed"
  | "skipped";

// Avatar component for ActionItemExecutionCard
export function ActionItemExecutionCardAvatar({
  status = "started",
  className,
  style,
}: {
  status?: ActionItemExecutionStatus;
  className?: string;
  style?: React.CSSProperties;
}) {
  const getIcon = () => {
    switch (status) {
      case "running":
        return (
          <Loader2
            className="h-5 w-5 text-lc-warning dark:text-lc-warning animate-spin"
            style={style}
          />
        );
      case "needs_attention":
        return (
          <AlertCircle
            className="h-5 w-5 text-lc-blocked dark:text-lc-blocked"
            style={style}
          />
        );
      case "completed":
        return (
          <CheckCircle
            className="h-5 w-5 text-lc-active dark:text-lc-active"
            style={style}
          />
        );
      case "failed":
        return (
          <XCircle
            className="h-5 w-5 text-lc-blocked dark:text-lc-blocked"
            style={style}
          />
        );
      case "skipped":
        return (
          <MinusCircle
            className="h-5 w-5 text-muted-foreground"
            style={style}
          />
        );
      case "started":
      default:
        return (
          <PlayCircle
            className="h-5 w-5 text-teal dark:text-teal"
            style={style}
          />
        );
    }
  };

  return (
    <BaseEntityCardIcon className={className} style={style}>
      {getIcon()}
    </BaseEntityCardIcon>
  );
}

export function ActionItemExecutionCard(props: ActionItemExecutionCardProps) {
  const {
    action_item_name = "",
    status = "started",
    skip_reason,
    current_activity,
    isLoading = false,
    onClick,
    onDoubleClick,
    actions,
    processes,
    className,
    style,
  } = props;

  // Determine if we should show shimmer effect
  const showShimmer = isLoading || status === "running";

  // Sync shimmer and spin animations to start at the same time
  useEffect(() => {
    if (showShimmer) {
      for (const a of document.getAnimations()) {
        // @ts-expect-error animationName is not in the type
        if (a.animationName === "shimmer" || a.animationName === "spin") {
          a.startTime = 0;
        }
      }
    }
  }, [showShimmer]);

  // Build the subtitle text
  const getSubtitleText = () => {
    if (current_activity) {
      return current_activity;
    }
    switch (status) {
      case "running":
        return "Processing...";
      case "needs_attention":
        return "Waiting for your input";
      case "completed":
        return "Task completed successfully";
      case "failed":
        return "Task failed";
      case "skipped":
        return skip_reason ? `Skipped: ${skip_reason}` : "Skipped";
      case "started":
      default:
        return "Starting...";
    }
  };

  return (
    <BaseEntityCard
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      processes={processes}
      className={className}
      style={style}
      align="center"
      actions={actions}
    >
      <ActionItemExecutionCardAvatar status={status} />

      <BaseEntityCardContent>
        <BaseEntityCardHeader>
          <BaseEntityCardTitle>{action_item_name}</BaseEntityCardTitle>
          <BaseEntityCardActions>
            {actions && actions.length > 0 && (
              <BaseEntityCardContextMenu actions={actions} />
            )}
          </BaseEntityCardActions>
        </BaseEntityCardHeader>

        <BaseEntityCardBody>
          <BaseEntityCardDescription
            className={cn({
              shimmer: showShimmer,
              "text-muted-foreground": showShimmer,
            })}
          >
            {getSubtitleText()}
          </BaseEntityCardDescription>
        </BaseEntityCardBody>
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}
